import clsx from 'clsx';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HUE_FILL, HUE_TEXT, HUE_TINT, type Hue } from './hue';

export interface TabItem<K extends string = string> {
  key: K;
  label: React.ReactNode;
  count?: number;
}

/**
 * Roving keyboard for a single-select group: arrows move focus and
 * selection (wrapping), Home/End jump to the ends. Only the selected item is
 * in the tab order.
 */
export function rovingKeyHandler<K extends string>(
  items: TabItem<K>[],
  value: K,
  onChange: (key: K) => void,
  keys: { next: string[]; prev: string[] },
) {
  return (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (items.length === 0) return;
    const idx = items.findIndex((i) => i.key === value);
    let nextIdx: number;
    if (keys.next.includes(e.key)) nextIdx = (idx + 1) % items.length;
    else if (keys.prev.includes(e.key))
      nextIdx = (idx - 1 + items.length) % items.length;
    else if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End') nextIdx = items.length - 1;
    else return;
    e.preventDefault();
    if (idx === -1 && keys.next.includes(e.key)) nextIdx = 0;
    const next = items[nextIdx];
    onChange(next.key);
    e.currentTarget
      .querySelector<HTMLElement>(`[data-key="${next.key}"]`)
      ?.focus();
  };
}

/** The item that receives Tab focus: the selected one, else the first. */
export const tabStop = <K extends string>(items: TabItem<K>[], value: K) =>
  items.some((i) => i.key === value) ? value : items[0]?.key;

/**
 * Position of the selected item inside `listRef`, for a sliding indicator.
 * Re-measured on resize (labels and fonts reflow). Null until measured, or
 * when nothing matches `selector`.
 */
function useIndicator(
  listRef: React.RefObject<HTMLElement>,
  selector: string,
  deps: unknown[],
) {
  const [indicator, setIndicator] = useState<{
    left: number;
    width: number;
  } | null>(null);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const measure = () => {
      const el = list.querySelector<HTMLElement>(selector);
      setIndicator(el ? { left: el.offsetLeft, width: el.offsetWidth } : null);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(list);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selector, ...deps]);

  return indicator;
}

const INDICATOR_MOTION =
  'pointer-events-none absolute left-0 h-0.5 rounded-full transition-[transform,width] duration-[160ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none';

/**
 * Tabs: underline style, used for switching *content in place* — views of
 * the same object, or the sections of a settings page.
 * Selected: hue text and a 2px indicator. Unselected: muted, no container.
 * One 2px indicator slides under the selected tab (160ms, no motion under
 * `prefers-reduced-motion`).
 * Keyboard: arrow keys move focus + selection (roving tabindex). The focus
 * ring is the global `:focus-visible` outline. A mouse click does not focus
 * the tab, so that ring does not linger and read as a second selected style.
 */
export function Tabs<K extends string>({
  items,
  value,
  onChange,
  className,
  size = 'md',
  spacing = 'compact',
  hue,
  'aria-label': ariaLabel,
}: {
  items: TabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  className?: string;
  size?: 'sm' | 'md';
  /**
   * `compact` is the in-page default. `relaxed` spreads a long nav bar
   * (platform admin) across the row when the labels do not already fill it.
   */
  spacing?: 'compact' | 'relaxed';
  /** Colours the selected label, its count and the indicator. */
  hue?: Hue;
  'aria-label'?: string;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const indicator = useIndicator(listRef, `[role="tab"][data-key="${value}"]`, [
    items,
  ]);

  const onKeyDown = rovingKeyHandler(items, value, onChange, {
    next: ['ArrowRight'],
    prev: ['ArrowLeft'],
  });
  const stop = tabStop(items, value);
  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={clsx(
        'relative flex items-end gap-1 border-b border-v2-line',
        spacing === 'relaxed' && 'justify-between',
        className,
      )}
    >
      {items.map((item) => {
        const selected = item.key === value;
        return (
          <button
            key={item.key}
            data-key={item.key}
            role="tab"
            type="button"
            aria-selected={selected}
            tabIndex={item.key === stop ? 0 : -1}
            onMouseDown={(e) => {
              if (e.button === 0) e.preventDefault();
            }}
            onClick={() => onChange(item.key)}
            className={clsx(
              'inline-flex shrink-0 items-center whitespace-nowrap rounded-v2-sm px-2 font-medium transition-colors',
              size === 'sm' ? 'h-8 text-v2-12' : 'h-9 text-v2-13',
              selected
                ? hue
                  ? HUE_TEXT[hue]
                  : 'text-v2-fg'
                : 'text-v2-fg-3 hover:bg-v2-sunken/70 hover:text-v2-fg',
              // Stands in for the indicator until the first measurement.
              !indicator &&
                selected &&
                'shadow-[inset_0_-2px_0_0_currentColor]',
            )}
          >
            {item.label}
            {typeof item.count === 'number' && (
              <span
                className={clsx(
                  'v2-num ml-1.5 text-v2-11',
                  hue
                    ? clsx(
                        'rounded-full px-1.5 font-semibold leading-[18px]',
                        selected ? HUE_TINT[hue] : 'bg-v2-sunken text-v2-fg-3',
                      )
                    : selected
                      ? 'text-v2-fg-3'
                      : 'text-v2-fg-4',
                )}
              >
                {item.count}
              </span>
            )}
          </button>
        );
      })}
      {indicator && (
        <span
          aria-hidden
          className={clsx(
            INDICATOR_MOTION,
            '-bottom-px',
            hue ? HUE_FILL[hue] : 'bg-v2-fg',
          )}
          style={{
            width: indicator.width,
            transform: `translateX(${indicator.left}px)`,
          }}
        />
      )}
    </div>
  );
}

export interface NavTabItem {
  to: string;
  label: React.ReactNode;
  /** Defaults to the path itself or anything below it. */
  active?: boolean;
}

/**
 * NavTabs: the pages of one product area (Events: Overview, Inquiries,
 * Pipeline…). Each tab is a link to its own route, and each page below
 * still carries its own `PageHeader` title.
 *
 * Unlike `Tabs`, which switch content inside a card, NavTabs stand on the
 * canvas above the page, at a larger size, with a hairline across the full
 * width. `trailing` holds area-wide controls (scope pickers, a role) at the
 * right end of the same bar. The bar scrolls sideways on narrow screens.
 */
export function NavTabs({
  items,
  trailing,
  className,
  'aria-label': ariaLabel,
}: {
  items: NavTabItem[];
  trailing?: React.ReactNode;
  className?: string;
  'aria-label': string;
}) {
  const { pathname } = useLocation();
  const listRef = useRef<HTMLDivElement>(null);
  const isActive = (item: NavTabItem) =>
    item.active ?? (pathname === item.to || pathname.startsWith(`${item.to}/`));
  const activeIndex = items.findIndex(isActive);
  const indicator = useIndicator(listRef, `[data-nav-index="${activeIndex}"]`, [
    items,
    pathname,
  ]);

  return (
    <div
      className={clsx(
        'flex min-h-11 shrink-0 items-center gap-x-4 border-b border-v2-line',
        className,
      )}
    >
      <nav
        aria-label={ariaLabel}
        className="-mb-px min-w-0 flex-1 self-stretch overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div
          ref={listRef}
          className="relative flex h-full w-max items-stretch gap-1"
        >
          {items.map((item, index) => {
            const active = index === activeIndex;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-nav-index={index}
                aria-current={active ? 'page' : undefined}
                className={clsx(
                  'group flex shrink-0 items-center whitespace-nowrap text-v2-14 font-medium transition-colors',
                  active ? 'text-v2-fg' : 'text-v2-fg-3 hover:text-v2-fg',
                  !indicator &&
                    active &&
                    'shadow-[inset_0_-2px_0_0_rgb(var(--v2-accent))]',
                )}
              >
                <span className="rounded-v2 px-2.5 py-1.5 group-hover:bg-v2-surface/70">
                  {item.label}
                </span>
              </Link>
            );
          })}
          {indicator && (
            <span
              aria-hidden
              className={clsx(INDICATOR_MOTION, 'bottom-0 bg-v2-accent')}
              style={{
                width: indicator.width,
                transform: `translateX(${indicator.left}px)`,
              }}
            />
          )}
        </div>
      </nav>
      {trailing && (
        <div className="flex shrink-0 items-center gap-3 py-1.5">
          {trailing}
        </div>
      )}
    </div>
  );
}

/**
 * Segmented control: for switching *queries* of a list (Inbox / Upcoming / All).
 * Compact, bordered group; selection is a filled segment.
 * Keyboard: radio-group pattern — arrow keys move focus + selection.
 */
export function Segmented<K extends string>({
  items,
  value,
  onChange,
  className,
  'aria-label': ariaLabel,
}: {
  items: TabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  className?: string;
  'aria-label'?: string;
}) {
  const onKeyDown = rovingKeyHandler(items, value, onChange, {
    next: ['ArrowRight', 'ArrowDown'],
    prev: ['ArrowLeft', 'ArrowUp'],
  });
  const stop = tabStop(items, value);
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={clsx(
        'inline-flex h-8 items-stretch rounded-v2 border border-v2-line-strong/80 bg-v2-surface p-0.5',
        className,
      )}
    >
      {items.map((item) => {
        const selected = item.key === value;
        return (
          <button
            key={item.key}
            data-key={item.key}
            role="radio"
            type="button"
            aria-checked={selected}
            tabIndex={item.key === stop ? 0 : -1}
            onClick={() => onChange(item.key)}
            className={clsx(
              'inline-flex items-center rounded-[4px] px-2.5 text-v2-12 font-medium transition-colors',
              selected
                ? 'bg-v2-fg text-v2-surface'
                : 'text-v2-fg-2 hover:bg-v2-sunken hover:text-v2-fg',
            )}
          >
            {item.label}
            {typeof item.count === 'number' && (
              <span
                className={clsx(
                  'v2-num ml-1.5 text-v2-11',
                  selected ? 'text-v2-surface/70' : 'text-v2-fg-4',
                )}
              >
                {item.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
